import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Tabs,
  Tab,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import { handleTitleCaseChange } from '../utils/textUtils';
import { ValidationRules, validateForm, hasErrors, formatErrors } from '../utils/validation';
import ValidatedFormControl from '../components/ValidatedFormControl';
import { createTooltipProps } from '../utils/fieldTooltips';

const EMPTY_SUPPLIER_FORM = {
  code: '',
  name: '',
  contact_name: '',
  email: '',
  telephone: '',
  line_1: '',
  line_2: '',
  line_3: '',
  town: '',
  county: '',
  postcode: '',
  country: '',
  fax: '',
  contact_telephone: '',
  depot: '',
  company_cis_reg: '',
  active: true,
};

function SupplierModal({ supplier, onClose, onSave }) {
  const [modalError, setModalError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_SUPPLIER_FORM);

  useEffect(() => {
    if (supplier) {
      setFormData({
        code: supplier.code || '',
        name: supplier.name || '',
        contact_name: supplier.contact_name || '',
        email: supplier.email || '',
        telephone: supplier.telephone || '',
        line_1: supplier.line_1 || '',
        line_2: supplier.line_2 || '',
        line_3: supplier.line_3 || '',
        town: supplier.town || '',
        county: supplier.county || '',
        postcode: supplier.postcode || '',
        country: supplier.country || '',
        fax: supplier.fax || '',
        contact_telephone: supplier.contact_telephone || '',
        depot: supplier.depot || '',
        company_cis_reg: supplier.company_cis_reg || '',
        active: supplier.active !== undefined ? supplier.active : true,
      });
    } else {
      setFormData(EMPTY_SUPPLIER_FORM);
    }
  }, [supplier]);

  const titleCaseFields = [
    'name',
    'contact_name',
    'line_1',
    'line_2',
    'line_3',
    'town',
    'county',
    'country',
  ];

  const handleChange = (e) => {
    handleTitleCaseChange(e, setFormData, formData, titleCaseFields);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Field length validations based on CSV structure
    const validations = [
      { field: 'code', maxLength: 8, label: 'Account No' },
      { field: 'name', maxLength: 60, label: 'Name' },
      { field: 'line_1', maxLength: 50, label: 'Address Line 1' },
      { field: 'line_2', maxLength: 50, label: 'Address Line 2' },
      { field: 'line_3', maxLength: 50, label: 'Address Line 3' },
      { field: 'town', maxLength: 50, label: 'Town' },
      { field: 'county', maxLength: 50, label: 'County' },
      { field: 'postcode', maxLength: 20, label: 'Postcode' },
      { field: 'country', maxLength: 120, label: 'Country' },
      { field: 'telephone', maxLength: 50, label: 'Telephone' },
      { field: 'fax', maxLength: 50, label: 'Fax' },
      { field: 'contact_name', maxLength: 130, label: 'Contact Name' },
      { field: 'contact_telephone', maxLength: 60, label: 'Contact Telephone' },
      { field: 'email', maxLength: 128, label: 'Email' },
    ];

    // Check field lengths
    for (const validation of validations) {
      if (formData[validation.field] && formData[validation.field].length > validation.maxLength) {
        alert(`${validation.label} must be ${validation.maxLength} characters or less`);
        return;
      }
    }

    // Email validation
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        alert('Please enter a valid email address');
        return;
      }
    }

    onSave(formData);
  };

  const handleCopy = () => {
    const copiedData = { ...formData };
    delete copiedData.id;
    copiedData.code = copiedData.code + '_copy';
    copiedData.name = copiedData.name + ' (Copy)';
    setFormData(copiedData);
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>{supplier ? 'Edit Supplier' : 'Add New Supplier'}</Modal.Title>
        {supplier && (
          <Button variant="secondary" onClick={handleCopy} className="ms-2">
            Copy Row
          </Button>
        )}
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Tabs defaultActiveKey="details" className="mb-3">
            <Tab eventKey="details" title="Details">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account No *</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('code')}>
                          Unique supplier account number
                        </Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="code"
                        value={formData.code}
                        onChange={handleChange}
                        maxLength="8"
                        required
                        {...createTooltipProps('code')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name *</Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip {...createTooltipProps('name')}>Supplier company name</Tooltip>
                      }
                    >
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        maxLength="20"
                        required
                        {...createTooltipProps('name')}
                      />
                    </OverlayTrigger>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Contact Person</Form.Label>
                    <Form.Control
                      type="text"
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      maxLength="130"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      maxLength="128"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="text"
                      name="telephone"
                      value={formData.telephone}
                      onChange={handleChange}
                      maxLength="50"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company CIS Registration</Form.Label>
                    <Form.Control
                      type="text"
                      name="company_cis_reg"
                      value={formData.company_cis_reg}
                      onChange={handleChange}
                      maxLength="255"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="address" title="Address">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 1 *</Form.Label>
                    <Form.Control
                      type="text"
                      name="line_1"
                      value={formData.line_1}
                      onChange={handleChange}
                      maxLength="50"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line 2</Form.Label>
                    <Form.Control
                      type="text"
                      name="line_2"
                      value={formData.line_2}
                      onChange={handleChange}
                      maxLength="50"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>City *</Form.Label>
                    <Form.Control
                      type="text"
                      name="town"
                      value={formData.town}
                      onChange={handleChange}
                      maxLength="50"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>County</Form.Label>
                    <Form.Control
                      type="text"
                      name="county"
                      value={formData.county}
                      onChange={handleChange}
                      maxLength="50"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Postcode</Form.Label>
                    <Form.Control
                      type="text"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleChange}
                      maxLength="20"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      maxLength="120"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="active"
                  label="Active"
                  checked={formData.active}
                  onChange={handleChange}
                />
              </Form.Group>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {supplier ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const fetchSuppliers = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/suppliers', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setSuppliers(response.data.suppliers);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchSuppliers(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchSuppliers(newPage, searchTerm);
  };

  const handleAddSupplier = () => {
    setEditingSupplier(null);
    setShowModal(true);
  };

  const handleEditSupplier = async (supplier) => {
    try {
      const response = await axios.get(`/api/suppliers/${supplier.id}`);
      setEditingSupplier(response.data);
      setShowModal(true);
    } catch (error) {
      setError('Failed to load supplier details');
    }
  };

  const handleSaveSupplier = async (supplierData) => {
    try {
      if (editingSupplier) {
        await axios.put(`/api/suppliers/${editingSupplier.id}`, supplierData);
        setSuccess('Supplier updated successfully');
      } else {
        await axios.post('/api/suppliers', supplierData);
        setSuccess('Supplier created successfully');
      }
      setShowModal(false);
      fetchSuppliers(pagination.page, searchTerm);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save supplier');
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await axios.delete(`/api/suppliers/${id}`);
        setSuccess('Supplier deleted successfully');
        fetchSuppliers(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete supplier');
      }
    }
  };

  const handleDeleteAllSuppliers = async () => {
    if (
      window.confirm('Are you sure you want to delete ALL suppliers? This action cannot be undone!')
    ) {
      if (
        window.confirm(
          'This will permanently delete all your supplier records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/suppliers');
          setSuccess(`All suppliers deleted successfully (${response.data.deletedCount} records)`);
          fetchSuppliers(1, '');
        } catch (error) {
          setError('Failed to delete all suppliers');
        }
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-people-fill me-3 text-primary" style={{ fontSize: '2rem' }}></i>
                <div>
                  <h1 className="mb-0">Suppliers</h1>
                  <p className="text-muted mb-0">Manage your suppliers database</p>
                </div>
              </div>
            </div>
            <div>
              <Button variant="primary" className="me-2" onClick={handleAddSupplier}>
                Add Supplier
              </Button>
              <Button variant="danger" onClick={handleDeleteAllSuppliers}>
                Delete All
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search suppliers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading suppliers...</div>
          ) : suppliers.length === 0 ? (
            <div className="text-center p-3">No suppliers found.</div>
          ) : (
            <>
              <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
                <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
                  <thead>
                    <tr>
                      <th>Account No</th>
                      <th>Name</th>
                      <th>Contact Person</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>City</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map((supplier) => (
                      <tr key={supplier.id}>
                        <td>{supplier.code}</td>
                        <td className="supplier-name">
                          <strong>{supplier.name}</strong>
                        </td>
                        <td className="contact-name">{supplier.contact_name}</td>
                        <td>{supplier.email}</td>
                        <td>{supplier.telephone}</td>
                        <td>{supplier.town}</td>
                        <td>
                          <Badge bg={supplier.active ? 'success' : 'secondary'}>
                            {supplier.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEditSupplier(supplier)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteSupplier(supplier.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <SupplierModal
          supplier={editingSupplier}
          onClose={() => setShowModal(false)}
          onSave={handleSaveSupplier}
        />
      )}
    </Container>
  );
}

export default Suppliers;
